import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

import { useAuth } from '../context/AuthContext';
import { billingApi } from '../services/api';
import { BillingPlan } from '../types';
import LegalFooter from '../components/LegalFooter';

const TIER_LABEL: Record<string, string> = { starter: 'Starter', growth: 'Growth', pro: 'Pro' };
const RECOMMENDED = 'growth'; // the single primary CTA (per UX: one primary action)

const Pricing: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [plans, setPlans] = useState<BillingPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyTier, setBusyTier] = useState<string | null>(null);

  const cancelled = params.get('status') === 'cancelled';

  useEffect(() => {
    billingApi.getPlans()
      .then(setPlans)
      .catch(() => setError('Could not load plans. Please refresh.'))
      .finally(() => setLoading(false));
  }, []);

  const choose = async (tier: string) => {
    setError(null);
    if (!isAuthenticated) {
      navigate(`/signup?plan=${tier}`);
      return;
    }
    setBusyTier(tier);
    try {
      const url = await billingApi.createCheckout(tier);
      window.location.assign(url);
    } catch {
      setError('We couldn’t start checkout. Please try again.');
      setBusyTier(null);
    }
  };

  return (
    <div style={wrap}>
      <div style={{ width: '100%', maxWidth: 940 }}>
        <div style={{ textAlign: 'center', marginBottom: 18 }}>
          <h1 style={title}>Choose your plan</h1>
          <p style={subtitle}>One analyzed phone number = one dial credit. No rollover; resets each cycle.</p>
        </div>

        {cancelled && (
          <div style={notice}>Checkout was cancelled — no charge was made. Pick a plan when you’re ready.</div>
        )}
        {error && <div style={errorBox}>{error}</div>}

        {loading ? (
          <div style={{ textAlign: 'center', color: 'var(--text-faint)', padding: 40 }}>Loading plans…</div>
        ) : (
          <div style={grid}>
            {plans.map((p) => {
              const primary = p.tier === RECOMMENDED;
              return (
                <div key={p.tier} style={{ ...card, ...(primary ? cardPrimary : null) }}>
                  {primary && <div style={badge}>Most popular</div>}
                  <div style={tierName}>{TIER_LABEL[p.tier] ?? p.tier}</div>
                  <div style={price}>{p.price_display}<span style={priceUnit}>/mo</span></div>
                  <div style={dials}>{p.included_dials.toLocaleString()} dials / cycle</div>
                  <button
                    onClick={() => choose(p.tier)}
                    disabled={busyTier === p.tier}
                    className={primary ? 'sidebar-new-run-btn' : ''}
                    style={primary ? { width: '100%', marginTop: 14 } : secondaryBtn}
                  >
                    {busyTier === p.tier ? 'Starting…' : isAuthenticated ? 'Subscribe' : 'Choose'}
                  </button>
                </div>
              );
            })}
          </div>
        )}

        <div style={{ textAlign: 'center', marginTop: 18 }}>
          <Link to={isAuthenticated ? '/' : '/login'} style={backLink}>
            {isAuthenticated ? 'Back to app' : 'Already have an account? Sign in'}
          </Link>
        </div>

        <LegalFooter />
      </div>
    </div>
  );
};

const wrap: React.CSSProperties = {
  minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
  background: 'var(--bg)', padding: 24,
};
const title: React.CSSProperties = {
  margin: 0, color: 'var(--amber)', fontFamily: 'var(--font-display)', letterSpacing: '0.06em', fontSize: 30,
};
const subtitle: React.CSSProperties = {
  margin: '8px 0 0', color: 'var(--text-faint)', fontFamily: 'var(--font-data)', fontSize: 12,
  letterSpacing: '0.1em', textTransform: 'uppercase',
};
const grid: React.CSSProperties = {
  display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, alignItems: 'stretch',
};
const card: React.CSSProperties = {
  position: 'relative', background: 'var(--surface)', border: '2px solid var(--border)',
  boxShadow: '6px 6px 0 rgba(0,0,0,0.5)', padding: '22px 20px', display: 'flex', flexDirection: 'column',
};
const cardPrimary: React.CSSProperties = {
  borderColor: 'var(--amber)', boxShadow: '6px 6px 0 rgba(0,0,0,0.6)',
};
const badge: React.CSSProperties = {
  position: 'absolute', top: -11, right: 14, background: 'var(--amber)', color: 'var(--navy)',
  fontFamily: 'var(--font-data)', fontSize: 10, fontWeight: 700, letterSpacing: '0.1em',
  textTransform: 'uppercase', padding: '3px 8px',
};
const tierName: React.CSSProperties = {
  color: 'var(--text-strong)', fontFamily: 'var(--font-display)', fontSize: 18, letterSpacing: '0.04em',
};
const price: React.CSSProperties = {
  marginTop: 8, color: 'var(--text)', fontFamily: 'var(--font-display)', fontSize: 28,
};
const priceUnit: React.CSSProperties = { fontSize: 13, color: 'var(--text-faint)', marginLeft: 4 };
const dials: React.CSSProperties = {
  marginTop: 6, color: 'var(--text-soft)', fontFamily: 'var(--font-data)', fontSize: 13,
  letterSpacing: '0.04em', flex: 1,
};
const secondaryBtn: React.CSSProperties = {
  width: '100%', marginTop: 14, padding: '9px 12px', background: 'transparent',
  color: 'var(--text)', border: '1px solid var(--border)', cursor: 'pointer',
  fontFamily: 'var(--font-data)', fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.08em',
};
const notice: React.CSSProperties = {
  marginBottom: 14, padding: '10px 12px', border: '1px solid var(--border)',
  background: 'var(--surface)', color: 'var(--text-soft)', fontSize: 13, textAlign: 'center',
};
const errorBox: React.CSSProperties = {
  marginBottom: 14, padding: '10px 12px', border: '1px solid var(--status-job-failed-fg)',
  background: 'var(--status-danger-bg)', color: 'var(--status-job-failed-fg)', fontSize: 13, textAlign: 'center',
};
const backLink: React.CSSProperties = {
  color: 'var(--text-faint)', fontSize: 12, textDecoration: 'none', letterSpacing: '0.04em',
};

export default Pricing;
